import { Component, Element, Event, EventEmitter, Fragment, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { AuthClientInstance, AuthTokenData, getAuthClient } from "@trumpf/ux.utils";
import { debounce } from "../../utils/debounce";
import {
    createHash,
    getCheckSessionIframeSrcdoc,
    getCodeChallenge,
    getCodeVerifier,
    getQueryString,
    getRandomString,
    getUrlOrigin
} from "./silent-auth-utils";

interface AuthPayload {
    client_id: string;
    code_challenge: string;
    code_challenge_method: "S256";
    prompt: "none" | "login";
    redirect_uri: string;
    response_type: "code";
    scope: string;
    state: string;
}

const TRUMPF_ISSUERS_PROD = ["https://identity.trumpf.com"];
const TRUMPF_ISSUERS_STAGE = ["https://identity-s.trumpf.com", "https://identity-t.trumpf.com"];
const TRUMPF_CALLBACK_URI_PROD = "https://cdn.trumpf.com/static/auth-callback.html";
const TRUMPF_CALLBACK_URI_STAGE = "https://cdn-q.trumpf.com/static/auth-callback.html";
const TRUMPF_CHECK_SESSION_URI_PROD = "https://cdn.trumpf.com/static/auth-checksession.html";
const TRUMPF_CHECK_SESSION_URI_STAGE = "https://cdn-q.trumpf.com/static/auth-checksession.html";

@Component({
    tag: "ux-u-silent-auth",
    styleUrl: "silent-auth.scss",
    shadow: false
})
export class SilentAuth {
    private authClient: AuthClientInstance;
    private authClientId: string;
    private authTokenData: AuthTokenData | null = null;
    private authIframe: HTMLIFrameElement | null = null;
    private authScopes: string[];
    private authCallbackHandler: (event: MessageEvent) => void;
    private authFailureTimeout: number;
    private codeVerifier: string;
    private checkSessionEnabled: boolean;
    private keepAliveTimeoutId: number;
    private uid: string;

    private _log = (msg: string) => {
        /* eslint-disable no-console */
        if (this.debug && typeof console !== "undefined" && typeof console.log === "function") {
            console.log(`[AUTH]: ${msg}`);
        }
        /* eslint-enable no-console */
    };

    /** The dom node */
    @Element() domElement!: HTMLUxUSilentAuthElement;

    /** URL of the identity provider */
    @Prop() readonly issuer: string = "https://identity.trumpf.com";
    /** Auth client id (is hidden when the component mounts) */
    @Prop() readonly clientId: string = "";
    /** Name of the client, used to get the scoped AuthClient instance */
    @Prop() readonly clientName: string = "";
    /** Requested scopes */
    @Prop() readonly scope: string = "oicd";
    /** Auth callback URL. Only needs to be provided for issuers other than https://identity*.trumpf.com */
    @Prop({ mutable: true }) redirectUri = "";
    /** Optional check-session iframe URL. The origin must match the redirectUri. */
    @Prop({ mutable: true }) checkSessionUri = "";
    /** Time in seconds after which the authentication flow is considered as failed */
    @Prop() readonly failedTimeout: number = 10;
    /** Time in seconds after which the authentication flow is rerun to keep the session alive */
    @Prop() readonly keepAliveTimeout: number = 600;
    /** If set to true, infos are logged to the console */
    @Prop() readonly debug: boolean;
    /** Automatically generated hash used to find related components in the DOM tree */
    @Prop({ reflect: true, mutable: true }) hash: string;
    /** Automatically set flag that indicates whether this instance is commissioned to perform authentication */
    @Prop({ reflect: true, mutable: true }) active: boolean;

    @State() loggedIn: boolean | undefined = undefined;
    @State() sessionState: string | undefined = undefined;
    @State() checkSessionActive = false;

    /** Emitted when the component is loaded to inform the delegated instance about scopes that may have changed */
    @Event({
        eventName: "uxSilentAuthAdded",
        bubbles: true,
        cancelable: false
    })
    instanceAdded!: EventEmitter<{
        hash: string;
        scope: string;
    }>;

    /** Emitted when the component is activated */
    @Event({
        eventName: "uxSilentAuthActivated",
        bubbles: true,
        cancelable: false
    })
    instanceActivated!: EventEmitter<{
        hash: string;
    }>;

    /**
     * Emitted after the authentication flow was completed.
     * At this point either an `access_token` was fetched successfully or silent authentication failed for some reason.
     */
    @Event()
    uxAuth!: EventEmitter<{
        success: boolean;
        error?: string | null;
        access_token?: string;
        expires_in?: number;
        id_token?: string;
        refresh_token?: string;
        token_type?: string;
    }>;

    /** If this is the active instance, silent code flow has to be performed if scopes have changed */
    @Listen("uxSilentAuthAdded", { target: "window" })
    onClientAdded(
        event: CustomEvent<{
            hash: string;
            scope: string;
        }>
    ) {
        if (event.detail.hash === this.hash && this.active && this.loggedIn) {
            if (Array.isArray(this.authScopes) && this.collectScopes().toString() !== this.authScopes.toString()) {
                this.stopCheckSession();
                this.loggedIn = undefined;
                this.initSilentAuthDebounced();
            }
        }
    }

    @Watch("active")
    handleActive() {
        if (this.active) {
            this.instanceActivated.emit({
                hash: this.hash
            });
            this.initSilentAuthDebounced();
        }
    }

    @Watch("loggedIn")
    handleLoggedIn() {
        if (!this.loggedIn) {
            this.stopCheckSession();
            this.sessionState = undefined;
        }
        if (this.loggedIn && this.checkSessionEnabled) {
            this.startCheckSession();
        }
    }

    componentWillLoad() {
        this.active = false;
        this.authClientId = this.clientId;
        this.domElement.removeAttribute("client-id");
        this.authClient = this.clientName.length > 0 ? getAuthClient(this.clientName) : getAuthClient();
        this.uid = getRandomString(32);
        this.hash = createHash([this.authClientId, this.clientName || "global"].join("-")).replace("-", "");
        if (!this.redirectUri) {
            if (TRUMPF_ISSUERS_PROD.includes(this.issuer)) {
                this.redirectUri = TRUMPF_CALLBACK_URI_PROD;
            } else if (TRUMPF_ISSUERS_STAGE.includes(this.issuer)) {
                this.redirectUri = TRUMPF_CALLBACK_URI_STAGE;
            }
        }
        if (!this.checkSessionUri) {
            if (TRUMPF_ISSUERS_PROD.includes(this.issuer)) {
                this.checkSessionUri = TRUMPF_CHECK_SESSION_URI_PROD;
            } else if (TRUMPF_ISSUERS_STAGE.includes(this.issuer)) {
                this.checkSessionUri = TRUMPF_CHECK_SESSION_URI_STAGE;
            }
        }
        this.checkSessionEnabled = !!this.checkSessionUri || window.origin === getUrlOrigin(this.redirectUri);
    }

    componentDidLoad() {
        this.activateDelegateDebounced();
        this.instanceAdded.emit({
            hash: this.hash,
            scope: this.scope
        });
    }

    disconnectedCallback() {
        this.stopCheckSession();
    }

    private activateDelegate = () => {
        this.findDelegatedInstance().active = true;
    };

    private activateDelegateDebounced = debounce(this.activateDelegate, 100);

    private initSilentAuth = async () => {
        this.stopCheckSession();
        this.cancelKeepAliveTimeout();
        if (!this.active) {
            return;
        }
        if (!this.authIframe) {
            return;
        }
        this._log("Init silent auth");
        this.loggedIn = undefined;
        const payload = await this.initAuthPayload();
        this.authScopes = payload.scope.split(" ");
        this.initAuthCallback(this.authIframe);
        this.authIframe.src = `${this.issuer}/oauth2/authorize?${getQueryString(payload)}`;

        // Timeout if the auth iframe did not receive a message within 10 seconds (e.g. because redirection failed for some reason).
        window.clearTimeout(this.authFailureTimeout);
        this.authFailureTimeout = window.setTimeout(() => {
            if (this.loggedIn === undefined) {
                this.uxAuth.emit({ success: false, error: "timeout" });
            }
        }, this.failedTimeout * 1000);
    };

    private initSilentAuthDebounced = debounce(this.initSilentAuth, 100);

    private initAuthCallback = (iframe: HTMLIFrameElement) => {
        if (typeof this.authCallbackHandler === "function") {
            window.removeEventListener("message", this.authCallbackHandler, true);
        }
        const authMessageHandler = (event: MessageEvent) => {
            if (event.origin !== getUrlOrigin(this.redirectUri)) {
                return;
            }
            if (event.source !== iframe.contentWindow) {
                return;
            }
            window.removeEventListener("message", this.authCallbackHandler, true);
            const { code, error } = event.data;
            if (typeof code === "string") {
                this._log("Got one-time code");
                this.sessionState = event.data.session_state;
                this.requestToken(code);
                return;
            }
            if (typeof error === "string") {
                this._log(`Error: ${error}`);
                this.authClient.deleteAccessToken();
                this.authTokenData = null;
                this.loggedIn = false;

                this.uxAuth.emit({ success: false, error: error });
                return;
            }
        };
        this.authCallbackHandler = authMessageHandler.bind(this);
        window.addEventListener("message", this.authCallbackHandler, true);
    };

    private findDelegatedInstance = (): HTMLUxUSilentAuthElement => {
        return (
            Array.from(document.querySelectorAll<HTMLUxUSilentAuthElement>(`ux-u-silent-auth[hash="${this.hash}"]`)).reduce(
                // @ts-expect-error Check for typing
                (_previousValue, currentValue) => {
                    if (currentValue.active) {
                        return currentValue;
                    }
                }
            ) || document.querySelector<HTMLUxUSilentAuthElement>(`ux-u-silent-auth[hash="${this.hash}"]`)
        );
    };

    private collectScopes = (): string => {
        const collectedScopes: string[] = this.scope.split(" ");
        Array.from(document.querySelectorAll<HTMLUxUSilentAuthElement>(`ux-u-silent-auth[hash="${this.hash}"]`)).forEach((el) => {
            el.scope.split(" ").forEach((elScope) => {
                if (!collectedScopes.includes(elScope)) {
                    collectedScopes.push(elScope);
                }
            });
        });
        return collectedScopes.sort().join(" ");
    };

    private initAuthPayload = async (): Promise<AuthPayload> => {
        this.codeVerifier = getCodeVerifier();
        const codeChallenge = await getCodeChallenge(this.codeVerifier);
        return {
            client_id: this.authClientId,
            code_challenge: codeChallenge,
            code_challenge_method: "S256",
            prompt: "none",
            redirect_uri: this.redirectUri,
            response_type: "code",
            scope: this.collectScopes(),
            state: window.top?.location.href || window.location.href
        };
    };

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    private requestToken = async (code: string): Promise<any> => {
        this._log("Request token with authorization_code grant");
        return this.sendTokenRequest({
            grant_type: "authorization_code",
            response_type: "token",
            redirect_uri: this.redirectUri,
            client_id: this.authClientId,
            scope: this.scope,
            code_verifier: this.codeVerifier,
            code
        });
    };

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    private sendTokenRequest = (data: any) => {
        return fetch(`${this.issuer}/oauth2/token`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            redirect: "follow",
            body: getQueryString(data)
        })
            .then((response) => response.json())
            .then((data) => {
                this._log("Got access token");
                this.handleTokenResponse(data);
                return data;
            });
    };

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    private handleTokenResponse = (data: any) => {
        if (typeof data === "object" && typeof data.access_token === "string") {
            this.authTokenData = data as AuthTokenData;
            let keepAliveDelay = this.keepAliveTimeout;
            if (this.authTokenData.expires_in > 0 && this.authTokenData.expires_in < keepAliveDelay) {
                keepAliveDelay = this.authTokenData.expires_in;
            }
            this.loggedIn = true;
            this.uxAuth.emit({ success: true, error: null, ...data });
            this._log(`Starting keep alive timeout (${keepAliveDelay} seconds)`);
            this.keepAliveTimeoutId = window.setTimeout(this.keepAlive, keepAliveDelay * 1000);
            // FIXME: We need the timeout as the access_token is not instantly valid in all scenarios
            window.setTimeout(() => {
                this.authClient.setTokenData(this.authTokenData as AuthTokenData);
            }, 2500);
        } else {
            this.authTokenData = null;
            this.loggedIn = false;
            this.uxAuth.emit({ success: false, error: "invalid_token_response" });
        }
    };

    private cancelKeepAliveTimeout = () => {
        window.clearTimeout(this.keepAliveTimeoutId);
    };

    private keepAlive = () => {
        this._log(`Keep alive ${new Date()}`);
        this.initSilentAuth();
    };

    private startCheckSession = () => {
        this.stopCheckSession();
        if (!this.active || !this.loggedIn || !this.checkSessionEnabled) {
            return;
        }
        this._log("Starting check-session");
        this.checkSessionActive = true;
        this.didReceiveFirstCheckSessionMessage = false;
        window.addEventListener("message", this.handleCheckSessionMessage, true);
    };

    private stopCheckSession = () => {
        if (this.checkSessionActive) {
            this._log("Stopping check-session");
            window.removeEventListener("message", this.handleCheckSessionMessage, true);
            this.checkSessionActive = false;
            this.didReceiveFirstCheckSessionMessage = false;
        }
    };

    private didReceiveFirstCheckSessionMessage = false;

    private handleCheckSessionMessage = (event: MessageEvent) => {
        if (event.origin !== window.origin && event.origin !== getUrlOrigin(this.redirectUri)) {
            return;
        }
        const messageParts = event.data.split(" ");
        const messageBody = messageParts[0];
        // Only handle valid message types
        if (!["unchanged", "changed", "error"].includes(messageBody)) {
            return;
        }
        // Only handle message with matching session
        if (messageParts[1] !== this.sessionState) {
            return;
        }
        if (!this.didReceiveFirstCheckSessionMessage) {
            this._log(`Received 1st check-session message: "${messageBody}"`);
        }
        this.didReceiveFirstCheckSessionMessage = true;
        switch (messageBody) {
            case "unchanged":
                break;
            case "changed":
                this._log("Session changed");
                this.stopCheckSession();
                this.initSilentAuth();
                break;
            case "error":
                this._log("Check session error");
                this.stopCheckSession();
                break;
            default:
                break;
        }
    };

    render() {
        if (!this.active) {
            return <Host class="ux-u-silent-auth" />;
        }
        let checkSessionSrc = "";
        let checkSessionSrcDoc = "";

        if (this.loggedIn && this.checkSessionEnabled && this.sessionState) {
            // OICD checksession iframe src
            const opIframeSrc = `${this.issuer}/oidc/checksession?${getQueryString({
                client_id: this.authClientId,
                redirect_uri: this.redirectUri
            })}`;
            if (window.origin === getUrlOrigin(this.redirectUri)) {
                // Same origin: render check-session iframe internally
                checkSessionSrcDoc = getCheckSessionIframeSrcdoc({
                    session_state: this.sessionState,
                    client_id: this.authClientId,
                    op_src: opIframeSrc
                });
            } else if (this.checkSessionUri) {
                // Different origin: load check-session iframe from checkSessionUri
                checkSessionSrc = `${this.checkSessionUri}?${getQueryString({
                    session_state: this.sessionState,
                    client_id: this.authClientId,
                    op_src: opIframeSrc,
                    origin: window.origin
                })}`;
            }
        }
        return (
            <Host class="ux-u-silent-auth">
                <iframe id={`silent-auth-${this.uid}`} ref={(el) => (this.authIframe = el as HTMLIFrameElement)} />
                {this.checkSessionEnabled && this.checkSessionActive && this.loggedIn && (
                    <Fragment>
                        {checkSessionSrcDoc && <iframe id={`silent-auth-check-session-${this.uid}`} srcDoc={checkSessionSrcDoc} />}
                        {!checkSessionSrcDoc && checkSessionSrc && (
                            <iframe id={`silent-auth-check-session-${this.uid}`} src={checkSessionSrc} />
                        )}
                    </Fragment>
                )}
            </Host>
        );
    }
}
